import { useState } from 'react';
import {
  TEMPLATE_FIELD_FILL_BY,
  TEMPLATE_FIELD_TYPES,
  type DocumentRenderSection,
  type TemplateEntryDef,
  type TemplateEntryField,
  type TemplateFieldFillBy,
  type TemplateFieldType,
  type TemplateStatus,
} from '@vivport/backend/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { assertToolSucceeded } from '@/api/templates-api';
import { useUpdateTemplateSection } from '@/hooks/useTemplateQueries';
import { toolErrorText } from '@/utils/tool-error';

interface TemplateEntryDefEditorProps {
  section: DocumentRenderSection;
  templateStatus: TemplateStatus;
}

interface CategoryDraft {
  label: string;
  items: string;
}

interface EntryFieldDraft {
  originalKey: string | null;
  key: string;
  keyEdited: boolean;
  label: string;
  type: TemplateFieldType;
  fillBy: TemplateFieldFillBy;
  required: boolean;
  options: string;
  allowOther: boolean;
  group: string;
}

interface EntryDefDraft {
  itemLabel: string;
  categories: CategoryDraft[];
  allowCustomItem: boolean;
  fields: TemplateEntryField[];
  photos: boolean;
  notes: boolean;
}

const FILL_BY_LABELS: Record<TemplateFieldFillBy, string> = {
  member: 'Member',
  writer: 'Writer',
  either: 'Either',
};

function slugify(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function lines(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

function emptyFieldDraft(): EntryFieldDraft {
  return {
    originalKey: null,
    key: '',
    keyEdited: false,
    label: '',
    type: 'text',
    fillBy: 'either',
    required: false,
    options: '',
    allowOther: false,
    group: '',
  };
}

function fieldDraftFrom(field: TemplateEntryField): EntryFieldDraft {
  return {
    originalKey: field.key,
    key: field.key,
    keyEdited: true,
    label: field.label,
    type: field.type,
    fillBy: field.fillBy,
    required: field.required === true,
    options: (field.options ?? []).join('\n'),
    allowOther: field.allowOther === true,
    group: field.group ?? '',
  };
}

function fieldFromDraft(draft: EntryFieldDraft): TemplateEntryField {
  const group = draft.group.trim();
  return {
    key: draft.key.trim(),
    label: draft.label.trim(),
    type: draft.type,
    fillBy: draft.fillBy,
    ...(draft.required ? { required: true } : {}),
    ...(draft.type === 'select' ? { options: lines(draft.options) } : {}),
    ...(draft.type === 'select' && draft.allowOther ? { allowOther: true } : {}),
    ...(group.length > 0 ? { group } : {}),
  };
}

function defDraftFrom(def: TemplateEntryDef | null): EntryDefDraft {
  return {
    itemLabel: def?.itemLabel ?? '',
    categories: (def?.categories ?? []).map((category) => ({
      label: category.label,
      items: category.items.join('\n'),
    })),
    allowCustomItem: def?.allowCustomItem === true,
    fields: def?.fields ?? [],
    photos: def?.photos ?? true,
    notes: def?.notes ?? true,
  };
}

function defFromDraft(draft: EntryDefDraft): TemplateEntryDef {
  const categories = draft.categories
    .map((category) => ({ label: category.label.trim(), items: lines(category.items) }))
    .filter((category) => category.label.length > 0);
  return {
    itemLabel: draft.itemLabel.trim(),
    ...(categories.length > 0 ? { categories } : {}),
    ...(draft.allowCustomItem ? { allowCustomItem: true } : {}),
    fields: draft.fields,
    photos: draft.photos,
    notes: draft.notes,
  };
}

// The definition is edited as a whole and written once through templates.updateTemplateSection,
// which validates it, refuses a section that carries a repeat table, and recomposes the preview.
export function TemplateEntryDefEditor({ section, templateStatus }: TemplateEntryDefEditorProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<EntryDefDraft | null>(null);
  const [fieldDraft, setFieldDraft] = useState<EntryFieldDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const updateSection = useUpdateTemplateSection();
  const facts = section.templateSection;

  if (facts === null || templateStatus === 'RETIRED') return null;
  const current = facts.entry;
  const editable = templateStatus === 'DRAFT';
  if (current === null && !editable) return null;

  const openEditor = () => {
    setDraft(defDraftFrom(current));
    setFieldDraft(null);
    setError(null);
    setOpen(true);
  };

  const closeEditor = () => {
    setOpen(false);
    setDraft(null);
    setFieldDraft(null);
    setError(null);
  };

  const write = async (entry: TemplateEntryDef | null) => {
    try {
      assertToolSucceeded(await updateSection.mutateAsync({ sectionDocumentId: section.id, entry }));
      closeEditor();
    } catch (caught) {
      setError(toolErrorText(caught));
    }
  };

  const saveDef = async () => {
    if (draft === null) return;
    await write(defFromDraft(draft));
  };

  const removeDef = async () => {
    if (!window.confirm(`Remove the entries from '${section.title || section.code}'? Its preview body is cleared.`)) {
      return;
    }
    await write(null);
  };

  const commitField = () => {
    if (draft === null || fieldDraft === null) return;
    const field = fieldFromDraft(fieldDraft);
    const fields =
      fieldDraft.originalKey === null
        ? [...draft.fields, field]
        : draft.fields.map((entry) => (entry.key === fieldDraft.originalKey ? field : entry));
    setDraft({ ...draft, fields });
    setFieldDraft(null);
  };

  const canCommitField =
    fieldDraft !== null && fieldDraft.key.trim().length > 0 && fieldDraft.label.trim().length > 0;
  const canSave = draft !== null && draft.itemLabel.trim().length > 0 && draft.fields.length > 0;

  return (
    <>
      <button type="button" onClick={openEditor}>
        <Badge variant="outline">{current === null ? 'Entries' : `Entries · ${current.itemLabel}`}</Badge>
      </button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) closeEditor();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Entries</DialogTitle>
            <DialogDescription>
              A repeating record for this section: one labelled block per item, with its own fields, photos and notes.
              {editable ? '' : ' Published templates show the definition read-only.'}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-3">
            {draft === null ? null : fieldDraft !== null ? (
              <div className="space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="entry-field-label">Label</Label>
                    <Input
                      id="entry-field-label"
                      value={fieldDraft.label}
                      onChange={(event) =>
                        setFieldDraft({
                          ...fieldDraft,
                          label: event.target.value,
                          key: fieldDraft.keyEdited ? fieldDraft.key : slugify(event.target.value),
                        })
                      }
                      disabled={!editable}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="entry-field-key">Key</Label>
                    <Input
                      id="entry-field-key"
                      value={fieldDraft.key}
                      onChange={(event) => setFieldDraft({ ...fieldDraft, key: event.target.value, keyEdited: true })}
                      disabled={!editable}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="entry-field-group">Group</Label>
                    <Input
                      id="entry-field-group"
                      value={fieldDraft.group}
                      onChange={(event) => setFieldDraft({ ...fieldDraft, group: event.target.value })}
                      placeholder="Optional heading the field sits under"
                      disabled={!editable}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Type</Label>
                    <Select
                      value={fieldDraft.type}
                      onValueChange={(value) => setFieldDraft({ ...fieldDraft, type: value as TemplateFieldType })}
                      disabled={!editable}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TEMPLATE_FIELD_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Filled by</Label>
                    <Select
                      value={fieldDraft.fillBy}
                      onValueChange={(value) => setFieldDraft({ ...fieldDraft, fillBy: value as TemplateFieldFillBy })}
                      disabled={!editable}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TEMPLATE_FIELD_FILL_BY.map((fillBy) => (
                          <SelectItem key={fillBy} value={fillBy}>
                            {FILL_BY_LABELS[fillBy]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="entry-field-required"
                    checked={fieldDraft.required}
                    onCheckedChange={(checked) => setFieldDraft({ ...fieldDraft, required: checked })}
                    disabled={!editable}
                  />
                  <Label htmlFor="entry-field-required">Required</Label>
                </div>
                {fieldDraft.type === 'select' ? (
                  <>
                    <div className="space-y-1.5">
                      <Label htmlFor="entry-field-options">Options (one per line)</Label>
                      <Textarea
                        id="entry-field-options"
                        rows={4}
                        value={fieldDraft.options}
                        onChange={(event) => setFieldDraft({ ...fieldDraft, options: event.target.value })}
                        disabled={!editable}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        id="entry-field-allow-other"
                        checked={fieldDraft.allowOther}
                        onCheckedChange={(checked) => setFieldDraft({ ...fieldDraft, allowOther: checked })}
                        disabled={!editable}
                      />
                      <Label htmlFor="entry-field-allow-other">Allow other</Label>
                    </div>
                  </>
                ) : null}
              </div>
            ) : (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="entry-item-label">Item label</Label>
                  <Input
                    id="entry-item-label"
                    value={draft.itemLabel}
                    onChange={(event) => setDraft({ ...draft, itemLabel: event.target.value })}
                    placeholder="What one entry is, e.g. Element"
                    disabled={!editable}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Categories</Label>
                  {draft.categories.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No categories — every entry is a free-form item.</p>
                  ) : (
                    draft.categories.map((category, index) => (
                      <div key={index} className="grid gap-2 rounded-md border p-2 sm:grid-cols-[1fr_1fr_auto]">
                        <Input
                          value={category.label}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              categories: draft.categories.map((entry, i) =>
                                i === index ? { ...entry, label: event.target.value } : entry
                              ),
                            })
                          }
                          placeholder="Category"
                          disabled={!editable}
                          className="h-8"
                        />
                        <Textarea
                          rows={2}
                          value={category.items}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              categories: draft.categories.map((entry, i) =>
                                i === index ? { ...entry, items: event.target.value } : entry
                              ),
                            })
                          }
                          placeholder="Items, one per line"
                          disabled={!editable}
                        />
                        {editable ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              setDraft({ ...draft, categories: draft.categories.filter((_, i) => i !== index) })
                            }
                          >
                            Remove
                          </Button>
                        ) : null}
                      </div>
                    ))
                  )}
                  {editable ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setDraft({ ...draft, categories: [...draft.categories, { label: '', items: '' }] })
                      }
                    >
                      Add category
                    </Button>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="entry-allow-custom"
                    checked={draft.allowCustomItem}
                    onCheckedChange={(checked) => setDraft({ ...draft, allowCustomItem: checked })}
                    disabled={!editable}
                  />
                  <Label htmlFor="entry-allow-custom">Allow items not in the category lists</Label>
                </div>
                <div className="space-y-2">
                  <Label>Fields</Label>
                  {draft.fields.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No fields yet — an entry needs at least one.</p>
                  ) : (
                    <ul className="divide-y rounded-md border text-sm">
                      {draft.fields.map((field) => (
                        <li key={field.key} className="flex flex-wrap items-center gap-2 px-3 py-2">
                          <span className="font-medium">{field.label}</span>
                          <code className="text-xs text-muted-foreground">{field.key}</code>
                          {field.group !== undefined ? <Badge variant="secondary">{field.group}</Badge> : null}
                          <Badge variant="secondary">{field.type}</Badge>
                          <Badge variant="secondary">{FILL_BY_LABELS[field.fillBy]}</Badge>
                          {field.required === true ? <Badge variant="outline">required</Badge> : null}
                          {field.type === 'select' ? (
                            <Badge variant="outline">{(field.options ?? []).length} option(s)</Badge>
                          ) : null}
                          <span className="ml-auto flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => setFieldDraft(fieldDraftFrom(field))}>
                              {editable ? 'Edit' : 'View'}
                            </Button>
                            {editable ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() =>
                                  setDraft({ ...draft, fields: draft.fields.filter((entry) => entry.key !== field.key) })
                                }
                              >
                                Remove
                              </Button>
                            ) : null}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {editable ? (
                    <Button size="sm" variant="outline" onClick={() => setFieldDraft(emptyFieldDraft())}>
                      Add field
                    </Button>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="entry-photos"
                      checked={draft.photos}
                      onCheckedChange={(checked) => setDraft({ ...draft, photos: checked })}
                      disabled={!editable}
                    />
                    <Label htmlFor="entry-photos">Photos</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="entry-notes"
                      checked={draft.notes}
                      onCheckedChange={(checked) => setDraft({ ...draft, notes: checked })}
                      disabled={!editable}
                    />
                    <Label htmlFor="entry-notes">Notes</Label>
                  </div>
                </div>
              </>
            )}
            {error !== null ? <p className="text-sm text-destructive">{error}</p> : null}
          </DialogBody>
          <DialogFooter>
            {fieldDraft !== null ? (
              <>
                <Button variant="ghost" onClick={() => setFieldDraft(null)}>
                  Back
                </Button>
                {editable ? (
                  <Button onClick={commitField} disabled={!canCommitField}>
                    Done
                  </Button>
                ) : null}
              </>
            ) : (
              <>
                {editable && current !== null ? (
                  <Button variant="ghost" onClick={() => void removeDef()} disabled={updateSection.isPending}>
                    Remove entries
                  </Button>
                ) : null}
                <Button variant="ghost" onClick={closeEditor} disabled={updateSection.isPending}>
                  {editable ? 'Cancel' : 'Close'}
                </Button>
                {editable ? (
                  <Button onClick={() => void saveDef()} disabled={!canSave || updateSection.isPending}>
                    Save
                  </Button>
                ) : null}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
